/**
 * RecordingList Component
 * Grid display of recordings with loading and empty states
 */

import { RecordingCard } from './RecordingCard';
import { Loader2, Video } from 'lucide-react';
import { useRecordings } from '../hooks/useRecordings';
import type { Recording } from '../types/recording';

interface RecordingListProps {
  onPlayRecording?: (recording: Recording) => void;
}

export function RecordingList({ onPlayRecording }: RecordingListProps) {
  const { data, isLoading, isError, error } = useRecordings(1, 20);

  // Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto" />
          <p className="text-gray-600 mt-2">Loading recordings...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Video className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mt-4">
            Failed to load recordings
          </h3>
          <p className="text-gray-600 mt-1">
            {error?.message || 'An error occurred while fetching your recordings'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-purple-600 hover:text-purple-700 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Empty State
  if (!data?.recordings || data.recordings.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
            <Video className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mt-4">
            No recordings yet
          </h3>
          <p className="text-gray-600 mt-1">
            Upload your first sales call recording to get started
          </p>
        </div>
      </div>
    );
  }

  // Grid Display
  return (
    <div className="space-y-4">
      {/* Results Count */}
      <p className="text-sm text-gray-600">
        {data.total} {data.total === 1 ? 'recording' : 'recordings'}
      </p>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.recordings.map((recording) => (
          <RecordingCard
            key={recording.id}
            recording={recording}
            onPlay={onPlayRecording}
          />
        ))}
      </div>

      {/* TODO: Add pagination if needed */}
      {data.total > data.pageSize && (
        <div className="text-center text-sm text-gray-500 pt-4">
          Showing {data.recordings.length} of {data.total} recordings
        </div>
      )}
    </div>
  );
}
